// #region This code is about the rule references
import { RULE_PROPERTY, RULE_TYPE } from '@/constants'
import uniqueId from 'lodash/uniqueId'
import _cloneDeep from 'lodash/cloneDeep'
import store from '@/employees/store/index'
/**
 * This will delete al reference property of all the rules in the baseRule
 *
 * @param {baseRuleObject} rule
 * @returns {baseRuleObject}
 */
export function removeReferences (rule) {
  const ruleClone = _cloneDeep(rule)
  removeReference(ruleClone.rule)
  return ruleClone
}
function generateReference () {
  return uniqueId('rule-')
}
/**
 * @param {Rule} ruleClone
 */
function removeReference (ruleClone) {
  delete ruleClone.reference

  if (ruleClone.description) {
    delete ruleClone.description.reference
  }

  if ((isCompositeType(ruleClone) || isOverlapType(ruleClone)) && ruleClone.rules) {
    for (let i = 0; i < ruleClone.rules.length; i++) {
      removeReference(ruleClone.rules[i])
    }
  } else if (isAggregateType(ruleClone) && ruleClone.values) {
    for (let i = 0; i < ruleClone.values.length; i++) {
      removeReference(ruleClone.values[i].rule)
    }
  } else if (isIfElseType(ruleClone)) {
    removeReference(ruleClone.if)
    removeReference(ruleClone.then)
    if (ruleClone.else) {
      removeReference(ruleClone.else)
    }
  } else if (isNotType(ruleClone)) {
    removeReference(ruleClone.rule)
  }
}
/**
 * This function will add references to the rules, to enable interactivity on the rules
 *
 * @param {baseRuleObject} rule
 * @returns {baseRuleObject}
 */
export function addReferences (rule) {
  const ruleClone = _cloneDeep(rule)
  addReference(ruleClone.rule)
  return ruleClone
}
/**
 * @param {Rule} ruleClone
 */
function addReference (ruleClone) {
  const reference = generateReference()
  ruleClone.reference = reference
  addOperator(ruleClone)
  setDisplayRuleForAddReference(ruleClone.type, reference)

  if (ruleClone.description) {
    ruleClone.description.reference = generateReference()
    setDisplayRuleForAddReference(RULE_TYPE.description, ruleClone.description.reference)
  }

  if ((isCompositeType(ruleClone) || isOverlapType(ruleClone)) && ruleClone.rules) {
    for (let i = 0; i < ruleClone.rules.length; i++) {
      addReference(ruleClone.rules[i])
    }
  } else if (isAggregateType(ruleClone) && ruleClone.values) {
    for (let i = 0; i < ruleClone.values.length; i++) {
      addReference(ruleClone.values[i].rule)
    }
  } else if (isIfElseType(ruleClone)) {
    addReference(ruleClone.if)
    addReference(ruleClone.then)
    if (ruleClone.else) {
      addReference(ruleClone.else)
    }
  } else if (isNotType(ruleClone)) {
    addReference(ruleClone.rule)
  }
}
/**
 * @param {RULE_TYPE} type
 * @param {string} reference
 */
function setDisplayRuleForAddReference (type, reference) {
  switch (type) {
    case RULE_TYPE.composite:
    case RULE_TYPE.overlap:
      setDisplayRule(reference)
      break
    case RULE_TYPE.if :
      setDisplayRule(reference + RULE_PROPERTY.if)
      setDisplayRule(reference + RULE_PROPERTY.then)
      setDisplayRule(reference + RULE_PROPERTY.else)
      break
    case RULE_TYPE.description:
      setDisplayRule(reference + RULE_PROPERTY.description)
      break
    case RULE_TYPE.not :setDisplayRule(reference)
      break
  }
}
function addOperator (ruleClone) {
  if (isCompositeType(ruleClone) && !ruleClone.operator) {
    ruleClone.operator = 'and'
  }
  if (isFilterType(ruleClone)) {
    parameterIsDefinedAndMissesOperator(ruleClone.filter.labels)
    parameterIsDefinedAndMissesOperator(ruleClone.filter.answers)
    parameterIsDefinedAndMissesOperator(ruleClone.filter.questions)
  }
}
function parameterIsDefinedAndMissesOperator (parameter) {
  if (parameter && !parameter.operator) {
    parameter.operator = 'or'
  }
}
// #endregion

// #region Check rule type
/**
 * @typedef RuleFilterQuestionOrAnswer
 * @property {Array<number>} in
 * @property {string} [operator]
 * @property {number} [count]
 */
/**
 * @typedef RuleFilter
 * @property {Array<RuleFilterQuestionOrAnswer>} [questions]
 * @property {Array<RuleFilterQuestionOrAnswer>} [answers]
 * @property {Array} [allOf]
 */
/**
 * @typedef RuleDescription
 * @property {string} reference
 * @property {string} nl
 * @property {string} en
 */
/**
 * @typedef Rule
 * @property {string} reference
 * @property {string} type
 * @property {RuleDescription} [description]
 * @property {RuleFilter} [filter]
 */
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @returns {rule is CompositeRule}
 */
function isCompositeType (rule) {
  return rule.type === RULE_TYPE.composite
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @return {boolean}
 */
function isOverlapType (rule) {
  return rule.type === RULE_TYPE.overlap
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @return {rule is AggregationRule}
 */
function isAggregateType (rule) {
  return rule.type === RULE_TYPE.aggregate
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @returns {boolean}
 */
function isIfElseType (rule) {
  return rule.type === RULE_TYPE.if
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @returns {boolean}
 */
function isNotType (rule) {
  return rule.type === RULE_TYPE.not
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @returns {boolean}
 */
function isFilterType (rule) {
  return rule.filter !== undefined
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @returns {boolean}
 */
function isStructureRule (rule) {
  return isCompositeType(rule) ||
    isOverlapType(rule) ||
    isIfElseType(rule) ||
    isAggregateType(rule) ||
    isNotType(rule)
}
/**
 * Check rule type
 *
 * @param {Rule} rule
 * @returns {boolean}
 */
function isDescriptionType (rule) {
  return rule.description !== undefined
}
// #endregion

// #region These function Either delete rules and properties of rules
/**
 * Will delete an property at location based on the reference and property
 *
 * @param {Rule} parentRule
 * @param {string} ruleReference
 * @returns {void}
 */
export function deleteRule (parentRule, reference) {
  if ((isCompositeType(parentRule) || isOverlapType(parentRule)) && parentRule.rules) {
    let spliceIndex = null
    for (let i = 0; i < parentRule.rules.length; i++) {
      if (parentRule.rules[i].reference === reference) {
        spliceIndex = i
        break
      } else if (isStructureRule(parentRule.rules[i])) {
        deleteRule(parentRule.rules[i], reference)
      }
    }
    if (spliceIndex !== null) {
      const isIfElse = isIfElseType(parentRule.rules[spliceIndex])
      parentRule.rules.splice(spliceIndex, 1)
      deleteRuleDeleteDisplayRule(isIfElse, reference)
    }
  } else if (isAggregateType(parentRule) && parentRule.values) {
    let spliceIndex = null
    for (let i = 0; i < parentRule.values.length; i++) {
      if (parentRule.values[i].rule.reference === reference) {
        spliceIndex = i
        break
      } else if (isStructureRule(parentRule.values[i].rule)) {
        deleteRule(parentRule.values[i].rule, reference)
      }
    }
    if (spliceIndex !== null) {
      const isIfElse = isIfElseType(parentRule.values[spliceIndex].rule)
      parentRule.values.splice(spliceIndex, 1)
      deleteRuleDeleteDisplayRule(isIfElse, reference)
    }
  } else if (isIfElseType(parentRule)) {
    deleteRule(parentRule.if, reference)
    deleteRule(parentRule.then, reference)
    if (parentRule.else) {
      deleteRule(parentRule.else, reference)
    }
  } else if (isNotType(parentRule)) {
    deleteRule(parentRule.rule, reference)
  }
}
function deleteRuleDeleteDisplayRule (isIfElse, reference) {
  if (isIfElse) {
    deleteDisplayRule(reference + 'if')
    deleteDisplayRule(reference + 'then')
    deleteDisplayRule(reference + 'else')
  } else {
    deleteDisplayRule(reference)
  }
}
/**
 * Will delete an property at location based on the reference and property
 *
 * @param {Rule} parentRule
 * @param {string} ruleReference
 * @param {string} property
 * @returns {void}
 */
export function deleteRuleProperty (parentRule, ruleReference, property, value) {
  if (parentRule.reference === ruleReference) {
    delete parentRule[property]
  } else if (isFilterType(parentRule) && parentRule.filter.reference === ruleReference) {
    delete parentRule.filter[property]
  } else if ((isCompositeType(parentRule) || isOverlapType(parentRule)) && parentRule.rules) {
    for (let i = 0; i < parentRule.rules.length; i++) {
      deleteRuleProperty(parentRule.rules[i], ruleReference, property, value)
    }
  } else if (isAggregateType(parentRule) && parentRule.values) {
    for (let i = 0; i < parentRule.values.length; i++) {
      deleteRuleProperty(parentRule.values[i].rule, ruleReference, property, value)
    }
  } else if (isIfElseType(parentRule)) {
    deleteRuleProperty(parentRule.if, ruleReference, property, value)
    deleteRuleProperty(parentRule.then, ruleReference, property, value)
    if (parentRule.else) {
      deleteRuleProperty(parentRule.else, ruleReference, property, value)
    }
  } else if (isNotType(parentRule)) {
    deleteRuleProperty(parentRule.rule, ruleReference, property, value)
  }
}

// #endregion

// #region search parent add child
/**
 * Will add an rule at location based on the reference and the ruleEnum
 *
 * @param {Rule} parentRule
 * @param {string} reference
 * @param {Rule} rule
 * @param {string} ruleEnum
 * @returns {void}
 */
export function searchParentAddChild (
  parentRule,
  reference,
  rule,
  ruleEnum,
) {
  switch (parentRule.type) {
    case RULE_TYPE.composite:
    case RULE_TYPE.overlap:
      addRuleToCompositeRule(parentRule, reference, rule, ruleEnum)
      break
    case RULE_TYPE.aggregate:
      addRuleToAggregateeRule(parentRule, reference, rule, ruleEnum)
      break
    case RULE_TYPE.if:
      addRuleToIfRule(parentRule, reference, rule, ruleEnum)
      break
    case RULE_TYPE.not:
      addRuleToNotRule(parentRule, reference, rule, ruleEnum)
      break
  }
}
function addRuleToCompositeRule (
  parentRule,
  reference,
  rule,
  ruleEnum,
) {
  if (parentRule.reference && parentRule.reference === reference && parentRule.rules && (ruleEnum === RULE_TYPE.composite || ruleEnum === RULE_TYPE.overlap)) {
    parentRule.rules.push(rule)
  } else if (parentRule.rules) {
    for (let i = 0; i < parentRule.rules.length; i++) {
      searchParentAddChild(parentRule.rules[i], reference, rule, ruleEnum)
    }
  }
}
function addRuleToAggregateeRule (
  parentRule,
  reference,
  rule,
  ruleEnum,
) {
  if (parentRule.reference && parentRule.reference === reference && parentRule.values && ruleEnum === RULE_TYPE.aggregate) {
    parentRule.values.push({ rule, value: 1 })
  } else if (parentRule.values) {
    for (let i = 0; i < parentRule.values.length; i++) {
      searchParentAddChild(parentRule.values[i].rule, reference, rule, ruleEnum)
    }
  }
}
function addRuleToIfRule (
  parentRule,
  reference,
  rule,
  ruleEnum,
) {
  if (parentRule.if.reference === reference && ruleEnum === RULE_TYPE.if) {
    parentRule.if = rule
  } else if (parentRule.then.reference === reference && ruleEnum === RULE_TYPE.if) {
    parentRule.then = rule
  } else if (parentRule.else !== undefined && parentRule.else.reference === reference && ruleEnum === RULE_TYPE.if) {
    parentRule.else = rule
  } else {
    searchParentAddChild(parentRule.if, reference, rule, ruleEnum)
    searchParentAddChild(parentRule.then, reference, rule, ruleEnum)
    if (parentRule.else) {
      searchParentAddChild(parentRule.else, reference, rule, ruleEnum)
    }
  }
}
function addRuleToNotRule (
  parentRule,
  reference,
  rule,
  ruleEnum,
) {
  if (parentRule.rule.reference === reference && ruleEnum === RULE_TYPE.not) {
    parentRule.rule = rule
  } else {
    searchParentAddChild(parentRule.rule, reference, rule, ruleEnum)
  }
}
// #endregion

// #region search rule add property
/**
 * Will add or update an property at location based on the reference and property
 *
 * @param {Rule} parentRule
 * @param {string} ruleReference
 * @param {string} property
 * @param {any} value
 * @returns {void}
 */
export function addRuleProperty (parentRule, ruleReference, property, value) {
  if (parentRule.reference === ruleReference) {
    parentRule[property] = value
  } else if (isDescriptionType(parentRule) && parentRule.description.reference === ruleReference) {
    if (property.includes('.nl')) {
      parentRule.description.nl = value
    } else if (property.includes('.en')) {
      parentRule.description.en = value
    } else if (property.includes('.all')) {
      parentRule.description.nl = value
      parentRule.description.en = value
    }
  } else if (isFilterType(parentRule) && parentRule.filter.reference === ruleReference) {
    //* used for adding filter options
    if (property.includes('.in')) {
      parentRule.filter[property.substring(0, property.length - 3)].in = value
    } else if (property.includes('.count')) {
      parentRule.filter[property.substring(0, property.length - 6)].count = value
    } else {
      parentRule.filter[property] = value
    }
  } else if ((isCompositeType(parentRule) || isOverlapType(parentRule)) && parentRule.rules) {
    for (let i = 0; i < parentRule.rules.length; i++) {
      addRuleProperty(parentRule.rules[i], ruleReference, property, value)
    }
  } else if (isAggregateType(parentRule) && parentRule.values) {
    for (let i = 0; i < parentRule.values.length; i++) {
      if (`${parentRule.reference}${parentRule.values[i].rule.reference}` === ruleReference) {
        parentRule.values[i][property] = value
        break
      } else {
        addRuleProperty(parentRule.values[i].rule, ruleReference, property, value)
      }
    }
  } else if (isIfElseType(parentRule)) {
    addRuleProperty(parentRule.if, ruleReference, property, value)
    addRuleProperty(parentRule.then, ruleReference, property, value)
    if (parentRule.else) {
      addRuleProperty(parentRule.else, ruleReference, property, value)
    }
  } else if (isNotType(parentRule)) {
    addRuleProperty(parentRule.rule, ruleReference, property, value)
  }
}
//* This part is about Updating properties of rules
export function ruleOperatorChange (parentRule, reference, ruleOperator, property) {
  if (parentRule.reference === reference) {
    parentRule.operator = ruleOperator
  } else if (isFilterType(parentRule) && parentRule.filter.reference === reference) {
    parentRule.filter[property].operator = ruleOperator
  } else if ((isCompositeType(parentRule) || isOverlapType(parentRule)) && parentRule.rules) {
    for (let i = 0; i < parentRule.rules.length; i++) {
      ruleOperatorChange(parentRule.rules[i], reference, ruleOperator, property)
    }
  } else if (isAggregateType(parentRule) && parentRule.values) {
    for (let i = 0; i < parentRule.values.length; i++) {
      ruleOperatorChange(parentRule.values[i].rule, reference, ruleOperator, property)
    }
  } else if (isIfElseType(parentRule)) {
    ruleOperatorChange(parentRule.if, reference, ruleOperator, property)
    ruleOperatorChange(parentRule.then, reference, ruleOperator, property)
    if (parentRule.else) {
      ruleOperatorChange(parentRule.else, reference, ruleOperator, property)
    }
  } else if (isNotType(parentRule)) {
    ruleOperatorChange(parentRule.rule, reference, ruleOperator, property)
  }
}
// #endregion

// #region Display rule
function setDisplayRule (reference) {
  store.dispatch('set_display_rule_reference', { reference, display: true })
}
function deleteDisplayRule (reference) {
  store.dispatch('delete_display_rule_reference', reference)
}
// #endregion

// #region Rule builder functions
/**
 * This function will create an base rule
 *
 * @param {Rule} rule1
 * @returns {baseRuleObject}
 */
export function buildBaseRule (rule1) {
  return {
    rule: {
      ...rule1,
    },
  }
}
/**
 * @typedef {Object} CompositeRule
 * @property {string} reference
 * @property {string} type
 * @property {string} operator
 * @property {Array<Object>} rules
 */
/**
 * This function will create an composite rule
 *
 * @returns {CompositeRule}
 */
export function buildCompositeRule () {
  const rules = []
  const rule = {
    reference: generateReference(),
    type: RULE_TYPE.composite,
    rules,
    operator: 'and',
  }
  setDisplayRule(rule.reference)
  return rule
}
/**
 * This function will create an overlap rule
 *
 * @returns {OverlapRuleObject}
 */
export function buildOverlapRule () {
  const rules = []
  const rule = {
    reference: generateReference(),
    type: RULE_TYPE.overlap,
    rules,
  }
  setDisplayRule(rule.reference)
  return rule
}
/**
 * @typedef {Object} AggregateRuleValue
 * @property {Object} rule
 * @property {number} value
 */
/**
 * @typedef {Object} AggregateRule
 * @property {string} reference
 * @property {string} type
 * @property {Array<AggregateRuleValue>} values
 */
/**
 * This function will create an overlap rule
 *
 * @returns { AggregateRule }
 */
export function buildAggregateRule () {
  const values = []
  const rule = {
    reference: generateReference(),
    type: RULE_TYPE.aggregate,
    values,
    min: 1,
  }
  setDisplayRule(rule.reference)
  return rule
}
/**
 * This function will create an base rule
 *
 * @param {Rule} rule1
 * @param {Rule} rule1
 * @returns {ifRuleObject}
 */
export function buildIfRule (rule1, rule2) {
  const rule = {
    reference: generateReference(),
    type: RULE_TYPE.if,
    if: {
      ...rule1,
    },
    then: {
      ...rule2,
    },
  }
  setDisplayRule(rule.reference + 'if')
  setDisplayRule(rule.reference + 'then')
  setDisplayRule(rule.reference + 'else')
  return rule
}
/**
 * This function will create an composite rule
 *
 * @returns {subscriptionsCountRuleObject}
 */
export function buildSubscriptionsCountRule () {
  return {
    reference: generateReference(),
    type: RULE_TYPE.subscriptions_count,
    min: 1,
  }
}
/**
 * This function will create an composite rule
 *
 * @returns {ectsCountRuleObject}
 */
export function buildEctsCountRule () {
  return {
    reference: generateReference(),
    type: RULE_TYPE.ects_count,
    min: 1,
  }
}
/**
 * This function will create an composite rule
 *
 * @returns {specificSubscriptionRuleObject}
 */
export function buildSpecificSubscriptionRule () {
  return {
    reference: generateReference(),
    type: RULE_TYPE.specific_subscription,
    subscribable: null,
  }
}

/**
 * This function will create a student Characteristic rule
 */
export function buildStudentCharacteristicRule () {
  return {
    reference: generateReference(),
    type: RULE_TYPE.student_characteristic,
    student_characteristic: null,
    values: [''],
    operator: 'or',
    count: 0,
  }
}
/**
 * This function will create an not rule
 *
 * @param {Rule} rule1
 * @returns {notTypeRuleObject}
 */
export function buildNotType (rule1) {
  const rule = {
    reference: generateReference(),
    type: RULE_TYPE.not,
    rule: { ...rule1 },
  }
  setDisplayRule(rule.reference)
  return rule
}
/**
 * This function will create an filter for an rule
 *
 * @returns {filterTypeRuleObject}
 */
export function buildFilterType () {
  return {
    reference: generateReference(),
    type: RULE_TYPE.filter,
  }
}
/**
 * This function will create an filter for an rule
 *
 * @returns {filterQuestionsTypeRuleObject}
 */
export function buildFilterQuestionsType () {
  return {
    in: [],
    operator: 'or',
  }
}
/**
 * This function will create an filter for an rule
 *
 * @returns {filterLabelsTypeRuleObject}
 */
export function buildFilterLabelsType () {
  return {
    in: [],
  }
}
/**
 * This function will create an filter for an rule
 *
 * @returns {RuleDescription}
 */
export function buildDescriptionType () {
  const description = {
    reference: generateReference(),
    nl: '',
    en: '',
  }

  setDisplayRule(description.reference + RULE_TYPE.description)

  return description
}
/**
 * This function will create an base rule
 *
 * @param {string} ruleEnum
 * @returns {Rule}
 */
export function buildRule (ruleEnum) {
  switch (ruleEnum) {
    case RULE_TYPE.subscriptions_count:
      return buildSubscriptionsCountRule()
    case RULE_TYPE.composite:
      return buildCompositeRule()
    case RULE_TYPE.if:
      return buildIfRule(buildSubscriptionsCountRule(), buildSubscriptionsCountRule())
    case RULE_TYPE.ects_count:
      return buildEctsCountRule()
    case RULE_TYPE.specific_subscription:
      return buildSpecificSubscriptionRule()
    case RULE_TYPE.not:
      return buildNotType(buildSubscriptionsCountRule())
    case RULE_TYPE.filter:
      return buildFilterType()
    case RULE_TYPE.overlap:
      return buildOverlapRule()
    case RULE_TYPE.aggregate:
      return buildAggregateRule()
    case RULE_TYPE.student_characteristic:
      return buildStudentCharacteristicRule()
    case RULE_TYPE.description:
      return buildDescriptionType()
    default:
      throw new Error('This rule type either does not exist or is not implemented yet in the ruleMixin buildRule function')
  }
}
// #endregion
